import {
  useV2ListServicesForAccountQuery,
  useV2ListGovernancesForAccountQuery,
  useV2ListProductsForAccountQuery,
} from "@generated";
import useInfiniteSelectOptions from "@components/FormsV2/Select2/useInfiniteSelectOptions";
import type { SearchSelectOption } from "@components/FormsV2/SearchSelect";
import type { ItemOption, ItemType } from "../types";

// Accounts can have more items than one page holds (NSI has 619 products),
// so the picker searches and pages instead of loading everything.
const PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 1000;

interface ListResponse {
  data?: any[];
  included?: any[];
}

type ListQueryHook = (
  args: any,
  options?: any
) => { data?: ListResponse; isFetching: boolean };

// Services are labelled with their category, which the list sideloads.
const serviceItem = (s: any, response?: ListResponse): ItemOption => {
  const name = s.attributes?.name ?? "";
  const catId = s.relationships?.["service-category"]?.data?.id;
  const category =
    catId != null
      ? (response?.included ?? []).find(
          (i: any) =>
            i.type === "service-categories" && String(i.id) === String(catId)
        )?.attributes?.["nested-name"] ?? ""
      : "";
  return {
    id: String(s.id),
    name,
    label: category ? `${name} (${category})` : name,
  };
};

const plainItem = (x: any): ItemOption => ({
  id: String(x.id),
  name: x.attributes?.name ?? "",
  label: x.attributes?.name ?? "",
});

const ITEM_SOURCES: Record<
  ItemType,
  {
    useQuery: ListQueryHook;
    params: Record<string, unknown>;
    toItem: (record: any, response?: ListResponse) => ItemOption;
  }
> = {
  Task: {
    useQuery: useV2ListServicesForAccountQuery,
    params: { include: ["service-category"] },
    toItem: serviceItem,
  },
  Governance: {
    useQuery: useV2ListGovernancesForAccountQuery,
    params: {},
    toItem: plainItem,
  },
  Material: {
    useQuery: useV2ListProductsForAccountQuery,
    params: {},
    toItem: plainItem,
  },
};

/**
 * Options for the Item select: searches the item type's records by name and
 * loads the next page as the menu scrolls. The query depends on itemType, so
 * the caller remounts (key={itemType}) when it changes.
 */
export const useItemOptions = (itemType: ItemType) => {
  const source = ITEM_SOURCES[itemType];
  const select = useInfiniteSelectOptions({
    useQuery: source.useQuery,
    params: source.params,
    pageSize: PAGE_SIZE,
    toOption: (record: any, response: ListResponse) => {
      const item = source.toItem(record, response);
      return { value: item.id, label: item.label };
    },
  });
  // toOption above only makes string values.
  return { ...select, options: select.options as SearchSelectOption[] };
};

export const byIdArgs = (
  ids: string[] = [],
  params: Record<string, unknown>
) => {
  // Array.from, not [...set]: production's loose preset-env compiles a spread
  // to [].concat(set), which wraps the Set instead of expanding it.
  const unique = Array.from(new Set(ids.filter(Boolean))).sort();
  return {
    // `filter[id]` and `include` pass through paramsSerializer untyped.
    args: {
      ...params,
      filter: { id: unique.join(",") },
      page: { size: Math.min(Math.max(unique.length, 1), MAX_PAGE_SIZE) },
    } as any,
    options: { skip: unique.length === 0, refetchOnMountOrArgChange: true },
  };
};

/**
 * Fetches just the items with these ids, so saved recommendations can show
 * names for items the picker hasn't paged in. Returns a lookup by type and id.
 */
export const useItemsById = (ids: Partial<Record<ItemType, string[]>>) => {
  const services = byIdArgs(ids.Task, ITEM_SOURCES.Task.params);
  const governances = byIdArgs(ids.Governance, ITEM_SOURCES.Governance.params);
  const products = byIdArgs(ids.Material, ITEM_SOURCES.Material.params);
  const { data: sData } = useV2ListServicesForAccountQuery(
    services.args,
    services.options
  );
  const { data: gData } = useV2ListGovernancesForAccountQuery(
    governances.args,
    governances.options
  );
  const { data: pData } = useV2ListProductsForAccountQuery(
    products.args,
    products.options
  );

  const found: Record<ItemType, ItemOption[]> = {
    Task: (sData?.data ?? []).map((s: any) => serviceItem(s, sData as any)),
    Governance: (gData?.data ?? []).map(plainItem),
    Material: (pData?.data ?? []).map(plainItem),
  };
  return (itemType: ItemType, id: string): ItemOption | undefined =>
    found[itemType].find((o) => o.id === id);
};
